// The last few things a viewer opened from search, kept in this browser per
// signed-in user. A shared front-desk computer must not show one person's
// searches to the next, so the key carries the user id and sign-out wipes
// every viewer's list.

import { useCallback, useSyncExternalStore } from 'react';

import { SEARCH_TARGETS, type SearchTargetKind } from './search-kinds';

const KEY_PREFIX = 'nebari:recent-searches:';
const MAX_RECENT = 3;

export interface RecentSearch {
  kind: SearchTargetKind;
  id: string;
  label: string;
  detail: string | null;
}

const EMPTY: RecentSearch[] = [];
const cache = new Map<string, RecentSearch[]>();
const listeners = new Set<() => void>();

function isRecentSearch(value: unknown): value is RecentSearch {
  const v = value as Partial<RecentSearch> | null;
  return (
    !!v &&
    typeof v.id === 'string' &&
    typeof v.label === 'string' &&
    typeof v.kind === 'string' &&
    v.kind in SEARCH_TARGETS
  );
}

function read(userId: string): RecentSearch[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(KEY_PREFIX + userId) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter(isRecentSearch).slice(0, MAX_RECENT) : EMPTY;
  } catch {
    // Storage can be blocked (private windows) or hold something unreadable;
    // either way the viewer simply has no recent searches.
    return EMPTY;
  }
}

function notify() {
  listeners.forEach((listener) => listener());
}

export function subscribeRecentSearches(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Newest first. Stable between changes, as `useSyncExternalStore` requires. */
export function getRecentSearches(userId: string): RecentSearch[] {
  let recent = cache.get(userId);
  if (!recent) {
    recent = read(userId);
    cache.set(userId, recent);
  }
  return recent;
}

export function rememberSearch(userId: string, item: RecentSearch) {
  const next = [
    item,
    ...getRecentSearches(userId).filter((r) => !(r.kind === item.kind && r.id === item.id)),
  ].slice(0, MAX_RECENT);
  cache.set(userId, next);
  try {
    localStorage.setItem(KEY_PREFIX + userId, JSON.stringify(next));
  } catch {
    // Best-effort: the in-memory list still serves this session.
  }
  notify();
}

/** Forgets every viewer's recent searches in this browser. Called on sign-out. */
export function clearRecentSearches() {
  cache.clear();
  try {
    const keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i));
    keys
      .filter((key) => key?.startsWith(KEY_PREFIX))
      .forEach((key) => localStorage.removeItem(key!));
  } catch {
    // Nothing stored that we can reach, so nothing to forget.
  }
  notify();
}

/** No user yet (still loading) means an empty, read-only list. */
export function useRecentSearches(userId: string | null) {
  const recent = useSyncExternalStore(subscribeRecentSearches, () =>
    userId ? getRecentSearches(userId) : EMPTY,
  );
  const remember = useCallback(
    (item: RecentSearch) => {
      if (userId) rememberSearch(userId, item);
    },
    [userId],
  );
  return { recent, remember };
}
